/**
 * AttachButton
 *
 * Botão de clipe + menu de anexo + upload, num componente só.
 *
 * Existe para que os quatro inboxes (WhatsApp pessoal, WhatsApp Business,
 * Instagram e Messenger) compartilhem exatamente o mesmo comportamento de
 * anexo. Antes cada tela tinha sua própria cópia do input e do handler, e as
 * cópias divergiram.
 *
 * Regra de envio, igual à do WhatsApp:
 *   cabe no limite               -> envia
 *   passou e dá para comprimir   -> o servidor comprime e envia
 *   passou e não dá              -> avisa o motivo e oferece "Enviar como arquivo"
 *
 * "Enviar como arquivo" reenvia o original sem comprimir, pelo limite de
 * documento (bem maior) — é como se manda um vídeo pesado preservando a
 * qualidade.
 */
import React, { useRef, useState } from 'react';
import { Paperclip, Loader2, FileUp, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import fileUploadService from '../../services/global/fileUploadService';
import AttachMenu from './AttachMenu';

const AttachButton = ({
  channel,
  allowDocument = true,
  disabled = false,
  buttonStyle = {},
  onUploaded,
  onError,
  onUploadingChange,
}) => {
  const { colors } = useTheme();
  const mediaInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  // Arquivos recusados por tamanho que ainda podem ir como documento.
  const [fallback, setFallback] = useState(null);

  const setBusy = (value) => {
    setUploading(value);
    onUploadingChange?.(value);
  };

  const handlePick = (kind) => {
    setFallback(null);
    if (kind === 'media') mediaInputRef.current?.click();
    else if (kind === 'document') documentInputRef.current?.click();
    else if (kind === 'camera') cameraInputRef.current?.click();
  };

  const upload = async (files, asDocument) => {
    if (!files.length) return;

    setBusy(true);
    setFallback(null);
    setStatus(
      asDocument
        ? 'Enviando arquivo...'
        : 'Enviando... arquivos grandes são comprimidos automaticamente.'
    );

    try {
      const results = await fileUploadService.uploadMultiple(
        files,
        (progress) => {
          if (progress.currentFile) {
            setStatus(`Enviando ${progress.currentFile}...`);
          }
        },
        { channel, asDocument }
      );

      if (results.uploaded.length > 0) {
        onUploaded?.(results.uploaded);
      }

      if (results.errors.length > 0) {
        // Recusa por tamanho vira oferta de reenvio como arquivo, em vez de um
        // erro seco: é a saída que o usuário quer na prática.
        const retryable = results.errors.filter((e) => e.canSendAsDocument);
        const fatal = results.errors.filter((e) => !e.canSendAsDocument);

        if (retryable.length > 0 && allowDocument) {
          const originals = files.filter((f) =>
            retryable.some((e) => e.filename === f.name)
          );
          setFallback({ files: originals, message: retryable[0].error });
        } else if (retryable.length > 0) {
          onError?.(retryable.map((e) => `${e.filename}: ${e.error}`).join('\n'));
        }

        if (fatal.length > 0) {
          onError?.(fatal.map((e) => `${e.filename}: ${e.error}`).join('\n'));
        }
      }
    } catch (err) {
      onError?.(err.message || 'Erro ao enviar o arquivo.');
    } finally {
      setBusy(false);
      setStatus(null);
    }
  };

  const handleSelect = (asDocument) => async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    await upload(picked, asDocument);
  };

  const styles = {
    wrapper: { position: 'relative', display: 'flex' },
    notice: {
      position: 'absolute',
      bottom: 'calc(100% + 8px)',
      left: 0,
      width: 320,
      backgroundColor: colors.bgPrimary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: 12,
      boxShadow: colors.shadowLg,
      padding: 12,
      zIndex: 50,
      fontSize: 12,
      color: colors.textPrimary,
    },
    noticeHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 8,
      marginBottom: 8,
    },
    noticeAction: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      width: '100%',
      padding: '8px 10px',
      borderRadius: 8,
      border: 'none',
      cursor: 'pointer',
      backgroundColor: colors.accentPrimary,
      color: '#ffffff',
      fontSize: 12,
      fontWeight: 600,
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      color: colors.textSecondary,
      cursor: 'pointer',
      padding: 0,
      lineHeight: 1,
    },
    statusBubble: {
      position: 'absolute',
      bottom: 'calc(100% + 8px)',
      left: 0,
      whiteSpace: 'nowrap',
      backgroundColor: colors.bgPrimary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: 8,
      boxShadow: colors.shadowMd,
      padding: '6px 10px',
      fontSize: 11,
      color: colors.textSecondary,
      zIndex: 50,
    },
  };

  return (
    <div style={styles.wrapper}>
      {/* Fotos e vídeos: comprime no servidor quando passa do limite */}
      <input
        ref={mediaInputRef}
        type="file"
        multiple
        accept={fileUploadService.getMediaAcceptString()}
        onChange={handleSelect(false)}
        style={{ display: 'none' }}
      />
      {/* Documento: qualquer arquivo, sem compressão */}
      <input
        ref={documentInputRef}
        type="file"
        multiple
        accept={fileUploadService.getDocumentAcceptString()}
        onChange={handleSelect(true)}
        style={{ display: 'none' }}
      />
      {/* Câmera (celular) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept={fileUploadService.getCameraAcceptString()}
        capture="environment"
        onChange={handleSelect(false)}
        style={{ display: 'none' }}
      />

      <AttachMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onPick={handlePick}
        allowDocument={allowDocument}
      />

      {uploading && status && <div style={styles.statusBubble}>{status}</div>}

      {fallback && (
        <div style={styles.notice}>
          <div style={styles.noticeHeader}>
            <span>{fallback.message}</span>
            <button
              type="button"
              style={styles.closeBtn}
              onClick={() => setFallback(null)}
              title="Fechar"
            >
              <X size={14} />
            </button>
          </div>
          <button
            type="button"
            style={styles.noticeAction}
            onClick={() => upload(fallback.files, true)}
          >
            <FileUp size={14} />
            Enviar como arquivo (qualidade original)
          </button>
        </div>
      )}

      <button
        type="button"
        style={{
          ...buttonStyle,
          ...(disabled || uploading ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
        }}
        title={uploading ? 'Enviando arquivos...' : 'Anexar'}
        onClick={() => setMenuOpen((v) => !v)}
        disabled={disabled || uploading}
      >
        {uploading ? (
          <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
        ) : (
          <Paperclip size={18} />
        )}
      </button>
    </div>
  );
};

export default AttachButton;
